/// <amd-module name="TFS/TeamProfilePanel/TeamProfilePanel" />
import * as React from "react";
import * as Resources from "TFS/TeamProfilePanel/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelCloseButton } from "VSSUI/Panel";
import { IBasicIdentityRef, Persona, PersonaSize } from "VSSUI/Persona";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";
import { ItemsTab } from "./ItemsTab";
import { MembersTab } from "./MembersTab";
import { Types } from "./TeamProfilePanelConstants";
import { ITeamProfilePanelService } from "./TeamProfilePanelService";

export interface TeamProfilePanelProps {
    onDismiss: () => void;
    teamId: string;
    teamName: string;
}

export class TeamProfilePanel extends VssComponent<TeamProfilePanelProps> {
    private selectedTabId: ObservableValue<string> = new ObservableValue(Resources.Items);
    private service: ITeamProfilePanelService;

    constructor(props: TeamProfilePanelProps, context: IVssComponentContext) {
        super(props, context);
        this.service = context.pageContext.getService<ITeamProfilePanelService>("ITeamProfilePanelService");
    }

    public render() {
        const { teamId, teamName } = this.props;
        return (
            <CustomPanel className="team-panel" onDismiss={this.props.onDismiss}>
                {this.renderCustomHeader()}
                <div className="team-panel-content flex-column">
                    <Observer provider={this.service.getMemberProvider(teamId)}>
                        {(props: { provider: TeamMember[] }) => {
                            return (
                                <TabBar
                                    ariaLabel={Resources.TabBar}
                                    onSelectedTabChanged={(newTabId) => this.onSelectedTabChanged(newTabId)}
                                    selectedTabId={this.selectedTabId}
                                    tabSize={TabSize.Tall}
                                >
                                    <Tab ariaLabel={Resources.Items} name={Resources.Items} id={Types.Items} />
                                    <Tab ariaLabel={Resources.Members} name={format(Resources.Members, props.provider.length)} id={Types.Members} />
                                </TabBar>
                            );
                        }}
                    </Observer>
                    <Observer selectedTabId={this.selectedTabId}>
                        {(props: { selectedTabId: string }) => {
                            return props.selectedTabId === Types.Members ? (
                                <MembersTab teamId={teamId} />
                            ) : (
                                <ItemsTab teamId={teamId} teamName={teamName} />
                            );
                        }}
                    </Observer>
                </div>
            </CustomPanel>
        );
    }

    private renderCustomHeader(): JSX.Element {
        const identity = {
            id: this.props.teamId,
            displayName: this.props.teamName,
            imageUrl: this.service.getIdentityImageUrl(this.props.teamId)
        } as IBasicIdentityRef;

        return (
            <CustomHeader className="team-panel-header-with-commandbar">
                <HeaderTitleArea>
                    <div className="flex">
                        <Persona className="margin-left-16" identity={identity} size={PersonaSize.size72} />
                        <div className="flex flex-column margin-left-16">
                            <label aria-label={this.props.teamName} className="font-size-l">
                                <Tooltip overflowOnly={true} text={this.props.teamName}>
                                    <span>{this.props.teamName}</span>
                                </Tooltip>
                            </label>
                            <label aria-label={this.props.teamName}>
                                <Tooltip overflowOnly={true} text={this.props.teamName}>
                                    <span>{this.service.getTeamProject()?.name}</span>
                                </Tooltip>
                            </label>
                            <div>
                                <FPSLink
                                    ariaLabel={Resources.TeamSettings}
                                    className="team-settings-link"
                                    href={this.service.getSettingsUrl(this.props.teamId)}
                                >
                                    {Resources.TeamSettings}
                                </FPSLink>
                            </div>
                        </div>
                    </div>
                </HeaderTitleArea>
                <PanelCloseButton onDismiss={this.props.onDismiss} />
            </CustomHeader>
        );
    }

    private onSelectedTabChanged(newTabId: string) {
        this.selectedTabId.value = newTabId;
    }
}

Components.add("ms.vss-tfs-web.team-profile-panel-component", TeamProfilePanel);
